import {
  Examination_Type,
  OrderType,
  PlatformTrialListOrderFields,
  useExaminationHeaderQuery,
  useExaminationInfoQuery,
  useTrialListQuery,
} from "@generated/graphql";
import Box from "@mui/material/Box";
import CircularLoading from "components/CircularLoading";
import useIsCreate from "hooks/useIsCreate";
import TrialList from "pages/Testing/view/Group/components/TrialList";
import qs from "qs";
import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import useGetCompetencyMapOptions from "../hooks/useGetCompetencyMapOptions";
import useGetScenarioOptions from "../hooks/useGetScenarioOptions";
import ExaminationContent from "./components/Content";
import Header from "./components/Header";

type SearchParams = {
  type: Examination_Type;
};

const ExaminationView = () => {
  const isCreate = useIsCreate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const params = useMemo(() => {
    return qs.parse(searchParams.toString()) as SearchParams;
  }, [searchParams]);
  const searchExaminationType = params.type;

  const {
    data: examinationHeaderData,
    loading: examinationHeaderLoading,
    refetch: examinationHeaderRefetch,
  } = useExaminationHeaderQuery({
    variables: {
      examinationId: id!,
    },
    skip: !id,
  });

  const examinationType = (isCreate ? searchExaminationType : examinationHeaderData?.examination.type) as
    | SearchParams["type"]
    | undefined;

  const trialsListPermanentFilter = {
    examinationId: id!,
  };

  const trialsListDefaultOrder = {
    field: PlatformTrialListOrderFields.CreatedAt,
    order: OrderType.Desc,
  };

  const {
    data: trialsListData,
    loading: trialsListLoading,
    refetch: trialsListRefetch,
  } = useTrialListQuery({
    variables: {
      page: 0,
      limit: 50,
      filter: trialsListPermanentFilter,
      order: trialsListDefaultOrder,
    },
    skip: !id,
  });

  const trials = trialsListData?.trials;

  const { data: examinationInfoData, loading: examinationInfoLoading } = useExaminationInfoQuery({
    variables: {
      examinationId: id!,
    },
    skip: !id,
    fetchPolicy: "cache-and-network",
  });

  const { competencyMapsOptions, loading: competencyMapsLoading } = useGetCompetencyMapOptions();
  const { scenariosOptions, loading: scenariosLoading } = useGetScenarioOptions();

  const loading =
    examinationHeaderLoading || examinationInfoLoading || (isCreate && (competencyMapsLoading || scenariosLoading));

  if (loading) return <CircularLoading />;

  return (
    <Box>
      <Header
        trialsData={trials}
        examination={examinationHeaderData?.examination}
        examinationType={examinationType}
        trialsListRefetch={trialsListRefetch}
        examinationHeaderRefetch={examinationHeaderRefetch}
      />
      <ExaminationContent
        examinationType={examinationType}
        data={examinationInfoData}
        trialsListRefetch={trialsListRefetch}
        competencyMapsOptions={competencyMapsOptions}
        competencyMapsLoading={competencyMapsLoading}
        scenariosOptions={scenariosOptions}
        scenariosLoading={scenariosLoading}
      />
      {!isCreate && examinationHeaderData?.examination && (
        <TrialList
          examination={examinationHeaderData.examination}
          trialsData={trials}
          loading={trialsListLoading}
          permanentFilter={trialsListPermanentFilter}
          defaultOrder={trialsListDefaultOrder}
          refetch={trialsListRefetch}
        />
      )}
    </Box>
  );
};
export default ExaminationView;
